<template>
    <canvas 
        ref="viewCanvas" 
        class="layer">
    </canvas>
</template>

<script>
import { createCanvas, scanCanvas, withCssContext } from './utils'

export default {
    name: 'CompositionLayer',
    props: {
        // quadratic resolution
        resolution: { 
            type: Number, 
            default: 1024 
        },
        // image smoothing for display scaling
        smoothing: { 
            type: Boolean, 
            default: true 
        },
        // when cropping, keep aspect ratio of selection and fit largest dimension into canvas
        cropKeepAspect: {
            type: Boolean,
            default: false
        }
    },
    data() {
        return {
            // tracked emptiness flag (fully transparent)
            empty: true,   

            // scaling
            dpr: 1,      
            cssSize: { w: 0, h: 0 },    

            // canvas view
            viewContext: null,

            // canvas buffer
            bufferCanvas: null,
            bufferContext: null,

            // canvas support
            supportCanvas: null,
            supportContext: null,

            // resize observer instance
            resizeObserver: null  
        };
    },
    emits: [
        'change'
    ],
    methods: {

        /// INITIALIZING

        init() 
        {
            this.initViewCanvas()
            this.initBufferCanvas()
            this.initSupportCanvas()
        },
        // view canvas: visible to the user (smoothed from buffer)
        initViewCanvas() 
        {
            const canvas = this.$refs.viewCanvas
            this.viewContext = canvas.getContext('2d')
            this.viewContext.imageSmoothingEnabled = this.smoothing
        },
        // buffer canvas: offscreen buffer for view (to use fixed resolution)
        initBufferCanvas() 
        {
            const { 
                canvas: bufferCanvas, 
                context: bufferContext 
            } = createCanvas(this.resolution, this.resolution, true) // will be read frequently
            this.bufferCanvas = bufferCanvas
            this.bufferContext = bufferContext
        },
        // support canvas: rendering support (for rotation or rendering snapshots)
        initSupportCanvas() 
        {
            const { 
                canvas: supportCanvas, 
                context: supportContext 
            } = createCanvas(1, 1) // will resize as needed
            this.supportCanvas = supportCanvas
            this.supportContext = supportContext
            this.supportContext.imageSmoothingEnabled = true
        },


        /// EVENT HANDLING

        handleResize() 
        { 
            // get canvas view
            const viewCanvas = this.$refs.viewCanvas
            if (!viewCanvas) return
            
            // calculate new dpr
            const newDpr = Math.max(1, window.devicePixelRatio || 1)

            // calculate new size
            const rect = viewCanvas.getBoundingClientRect()
            const cssW = Math.max(1, Math.round(rect.width))
            const cssH = Math.max(1, Math.round(rect.height))
            const newWidth = Math.max(1, Math.round(cssW * newDpr))
            const newHeight = Math.max(1, Math.round(cssH * newDpr))

            // check if we need to update
            const needDpr = newDpr !== this.dpr
            const needSize = viewCanvas.width !== newWidth || viewCanvas.height !== newHeight
            if (!needDpr && !needSize) return

            // update scaling
            this.dpr = newDpr
            this.cssSize = { w: cssW, h: cssH }

            // match the backing pixels to CSS * DPR
            viewCanvas.width = newWidth
            viewCanvas.height = newHeight

            // reset transform and smoothing on view context
            this.viewContext.setTransform(1, 0, 0, 1, 0, 0)
            this.viewContext.imageSmoothingEnabled = this.smoothing

            // flush any pending changes
            this._flush()
        },


        /// DISPOSING

        dispose() 
        {
            this.disposeViewCanvas()
            this.disposeBufferCanvas()
            this.disposeSupportCanvas()
        },
        disposeViewCanvas() 
        {
            this.viewContext = null
        },
        disposeBufferCanvas() 
        {
            if (this.bufferCanvas) {
                this.bufferCanvas.width = 0
                this.bufferCanvas.height = 0
            }
            this.bufferContext = null
            this.bufferCanvas = null
        },
        disposeSupportCanvas() 
        {
            if (this.supportCanvas) {
                this.supportCanvas.width = 0
                this.supportCanvas.height = 0
            }
            this.supportContext = null
            this.supportCanvas = null
        },


        /// IMPORTING AND EXPORTING

        async fromDataURL(dataUrl) 
        {
            // if no data url is provided
            if (!dataUrl) 
            {
                this.clear()
                return
            }
            
            // load image
            const img = await this.loadImage(dataUrl)

            // add image to context
            this.bufferContext.clearRect(0, 0, this.bufferCanvas.width, this.bufferCanvas.height)
            this.bufferContext.drawImage(img, 0, 0, this.bufferCanvas.width, this.bufferCanvas.height)

            // scan canvas
            this.scan()

            // apply changes and notify
            this._flush()
            this._emitChange()
        },
        toDataURL(opts = {}) 
        {
            // parse options
            let resolution = opts.resolution || this.bufferCanvas ? this.bufferCanvas.width : this.resolution
            let background = opts.background || null

            // validate resolution
            resolution = Math.max(1, resolution|0)

            // determine if scaling or background is needed
            const needScaling = !this.bufferCanvas || resolution !== this.bufferCanvas.width
            const needsBackground = !!background

            // fast export without scaling or background
            if (!needScaling && !needsBackground) {
                return this.bufferCanvas.toDataURL('image/png')
            }

            // prepare support canvas
            this._prepareSupportCanvas(resolution)

            // add background if needed
            if (needsBackground) {
                this.supportContext.save()
                this.supportContext.fillStyle = background
                this.supportContext.fillRect(0, 0, this.supportCanvas.width, this.supportCanvas.height)
                this.supportContext.restore()
            }

            // draw buffer to support canvas
            if (this.bufferCanvas) {
                this.supportContext.drawImage(this.bufferCanvas, 0, 0, this.supportCanvas.width, this.supportCanvas.height)
            }

            // export to data URL
            return this.supportCanvas.toDataURL('image/png')
        },
        async loadImage(src) 
        {
            return new Promise((resolve, reject) => {
                if (src instanceof HTMLImageElement) {
                    if (src.complete) resolve(src)
                    else {
                        src.onload = () => resolve(src)
                        src.onerror = reject
                    }
                    return
                }
                const img = new Image()
                img.onload = () => resolve(img)
                img.onerror = reject
                img.src = src
            })
        },


        /// DRAWING ON THE CANVAS

        withCssContext(fn, opts = {}) {
            const { markDirty = true } = opts
            withCssContext(this.bufferContext, this.bufferCanvas, this.cssSize, fn)
            if (markDirty) this._setEmpty(false, { force: true })
            this._flush()
        },


        /// LAYER OPERATIONS

        rotate(clockwise = true) 
        {
            // nothing to rotate
            if (this.empty) return 

            // prepare support canvas
            this._prepareSupportCanvas(this.resolution)
            this.supportContext.save()

            // apply rotation
            if (clockwise) {
                this.supportContext.translate(this.resolution, 0)
                this.supportContext.rotate(Math.PI / 2)
            } else {
                this.supportContext.translate(0, this.resolution)
                this.supportContext.rotate(-Math.PI / 2)
            }

            // draw buffer to support canvas
            this.supportContext.drawImage(this.bufferCanvas, 0, 0)
            this.supportContext.restore()

            // copy back
            this.bufferContext.clearRect(0, 0, this.resolution, this.resolution)
            this.bufferContext.drawImage(this.supportCanvas, 0, 0)

            // apply changes and notify
            this._flush()
            this._emitChange()
        },
        crop(rect, keepAspect=true)
        {
            // validate input
            if (!rect || rect.w <= 0 || rect.h <= 0) return

            // ensure we have a buffer
            const src = this.bufferCanvas
            if (!src || !src.width || !src.height) return

            // compute source pixel rect
            const sx = Math.round(rect.x * src.width)
            const sy = Math.round(rect.y * src.height)
            const sw = Math.max(1, Math.round(rect.w * src.width))
            const sh = Math.max(1, Math.round(rect.h * src.height))

            // draw into support canvas at native resolution
            this._prepareSupportCanvas(this.resolution)
            this.supportContext.save()
            this.supportContext.imageSmoothingEnabled = true
            this.supportContext.clearRect(0, 0, this.supportCanvas.width, this.supportCanvas.height)

            try {
                if (keepAspect) 
                {
                    // uniform scale so the larger dimension fits the square canvas
                    const destSize = this.supportCanvas.width // square
                    const scale = destSize / Math.max(sw, sh)
                    const dw = Math.max(1, Math.round(sw * scale))
                    const dh = Math.max(1, Math.round(sh * scale))
                    const dx = Math.round((destSize - dw) / 2)
                    const dy = Math.round((destSize - dh) / 2)
                    this.supportContext.drawImage(src, sx, sy, sw, sh, dx, dy, dw, dh)
                } else {
                    // stretch to fill square
                    this.supportContext.drawImage(src, sx, sy, sw, sh, 0, 0, this.supportCanvas.width, this.supportCanvas.height)
                }
            } catch (_) {}
            this.supportContext.restore()

            // replace buffer with cropped image
            this.bufferContext.clearRect(0, 0, this.resolution, this.resolution)
            this.bufferContext.drawImage(this.supportCanvas, 0, 0)

            // re-scan and apply changes
            this.scan()
            this._flush()
            this._emitChange()
        },
        scale(factor)
        {
            // validate
            if (this.empty) return
            if (!isFinite(factor) || factor <= 0) return
            if (factor === 1) return

            // prepare support canvas
            this._prepareSupportCanvas(this.resolution)

            // draw buffer into support with center-anchored scaling
            const cx = this.resolution / 2
            const cy = this.resolution / 2
            this.supportContext.save()
            this.supportContext.imageSmoothingEnabled = true
            this.supportContext.translate(cx, cy)
            this.supportContext.scale(factor, factor)
            this.supportContext.translate(-cx, -cy)
            this.supportContext.drawImage(this.bufferCanvas, 0, 0)
            this.supportContext.restore()

            // copy back to buffer
            this.bufferContext.clearRect(0, 0, this.resolution, this.resolution)
            this.bufferContext.drawImage(this.supportCanvas, 0, 0)

            // re-scan and apply changes
            this.scan()
            this._flush()
            this._emitChange()
        },
        async applyTransparency(dataUrl, opts = {})
        {
            if (!dataUrl) return
            if (!this.bufferCanvas || !this.bufferContext) return

            const { invert = false, union = false } = opts

            // load mask image
            const img = await this.loadImage(dataUrl)

            const width = this.bufferCanvas.width
            const height = this.bufferCanvas.height
            if (!width || !height) return

            // draw mask into support canvas at buffer resolution
            this._prepareSupportCanvas(width)
            this.supportContext.save()
            this.supportContext.imageSmoothingEnabled = true
            this.supportContext.drawImage(img, 0, 0, width, height)
            this.supportContext.restore()

            const maskImageData = this.supportContext.getImageData(0, 0, width, height)
            const mask = maskImageData.data

            const bufferImageData = this.bufferContext.getImageData(0, 0, width, height)
            const pixels = bufferImageData.data

            const useUnion = union !== false
            const invertMask = !!invert

            let changed = false
            for (let i = 0; i < pixels.length; i += 4) {
                const maskAlphaOriginal = mask[i + 3]
                let targetAlpha = invertMask ? 255 - maskAlphaOriginal : maskAlphaOriginal
                targetAlpha = Math.max(0, Math.min(255, targetAlpha))

                if (useUnion) {
                    const currentAlpha = pixels[i + 3]
                    const nextAlpha = Math.min(currentAlpha, targetAlpha)
                    if (nextAlpha !== currentAlpha) {
                        pixels[i + 3] = nextAlpha
                        changed = true
                    }
                } else {
                    if (pixels[i + 3] !== targetAlpha) {
                        pixels[i + 3] = targetAlpha
                        changed = true
                    }
                }
            }

            if (!changed) return

            this.bufferContext.putImageData(bufferImageData, 0, 0)
            this.scan()
            this._flush()
            this._emitChange()
        },
        scan() 
        {
            const empty = scanCanvas(this.bufferContext, this.bufferCanvas)
            this._setEmpty(empty)
            return empty
        },
        clear() 
        {
            const context = this.bufferContext;
            context.clearRect(0, 0, this.bufferCanvas.width, this.bufferCanvas.height)
            this._flush()
            this._setEmpty(true, { force: true })
        },


        /// DIRECT ACCESSORS (for parent)

        getBuffer() {
            return this.bufferCanvas
        },


        /// INTERNAL HELPERS

        _flush() 
        {
            // ensure view canvas and context are available
            const viewCanvas = this.$refs.viewCanvas
            if (!viewCanvas || !this.viewContext) return
            if (!this.bufferCanvas) return

            // clear and apply buffer to view
	        this.viewContext.clearRect(0, 0, viewCanvas.width, viewCanvas.height)
	        this.viewContext.drawImage(this.bufferCanvas, 0, 0, viewCanvas.width, viewCanvas.height)        
        },
        _setEmpty(empty, { force = false } = {}) 
        {
            const changed = this.empty !== empty
            this.empty = empty
            if (force || changed) this._emitChange()
        },
        _emitChange() 
        {
            this.$emit('change', { empty: this.empty })
        },
        _prepareSupportCanvas(resolution) 
        {
            this.supportCanvas.width = resolution
            this.supportCanvas.height = resolution
            this.supportContext.clearRect(0, 0, this.supportCanvas.width, this.supportCanvas.height)
        }
    },
    mounted() {
        // initialize component
        this.init()

        // register resize observer
        this.resizeObserver = new ResizeObserver(() => this.handleResize())
        this.resizeObserver.observe(this.$refs.viewCanvas)
    },
    beforeUnmount() {
        // unregister resize observer
        if (this.resizeObserver) this.resizeObserver.disconnect()

        // dispose component
        this.dispose()
    }
}
</script>